/**
 * SectionListItem — A single section card in the drag-and-drop tree list.
 *
 * Supports nesting: depth-based indentation, disclosure chevron for containers,
 * 2px left border accent, hierarchical index badges, and child count badge.
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, GripVertical } from 'lucide-react'
import { WIDGET_REGISTRY } from '@zaviago/storefront-config'
import { SectionContextMenu } from './SectionContextMenu'
import type { EditorSection } from './usePageSections'

interface SectionListItemProps {
  section: EditorSection
  /** Hierarchical label for the index badge, e.g. "1", "2.1", "2.2" */
  indexLabel: string
  /** Nesting depth (0 = root) */
  depth: number
  /** Whether this section is a container type (can hold children) */
  isContainer: boolean
  /** Whether this section has blocks (block-enabled widget) */
  hasBlocks?: boolean
  /** For containers/block sections: whether children are expanded/visible */
  isExpanded?: boolean
  /** For containers: number of direct children; for block sections: number of blocks */
  childCount?: number
  isSelected: boolean
  onSelect: (editorId: string) => void
  onDelete: (editorId: string) => void
  /** For containers/block sections: toggle expand/collapse */
  onToggleExpand?: (editorId: string) => void
  /** Full sections tree (for context menu container listing) */
  sections: EditorSection[]
  onMoveToContainer: (editorId: string, containerId: string) => void
  onMoveToRoot: (editorId: string) => void
  onAddChild: (parentEditorId: string, depth: number) => void
  onDuplicate: (editorId: string) => void
  /** Select the first block in a block-enabled section */
  onSelectFirstBlock?: (editorId: string) => void
}

/** Humanize a type key like "product-grid" → "Product Grid" */
function humanizeType(type: string): string {
  return type
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function SectionListItem({
  section,
  indexLabel,
  depth,
  isContainer,
  hasBlocks,
  isExpanded,
  childCount,
  isSelected,
  onSelect,
  onDelete,
  onToggleExpand,
  sections,
  onMoveToContainer,
  onMoveToRoot,
  onAddChild,
  onDuplicate,
  onSelectFirstBlock,
}: SectionListItemProps) {
  const isExpandable = isContainer || hasBlocks
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section._editorId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    marginLeft: depth * 20,
  }

  const widget = WIDGET_REGISTRY[section.type]
  const label = widget?.schema.label ?? humanizeType(section.type)

  return (
    <div
      ref={setNodeRef}
      style={style}
      role="treeitem"
      aria-level={depth + 1}
      {...(isExpandable ? { 'aria-expanded': isExpanded } : {})}
      className={`
        group flex items-center gap-2 rounded-sm border px-3 py-2.5
        transition-colors cursor-pointer
        ${isContainer ? 'border-l-2 border-l-sky-200' : ''}
        ${hasBlocks && !isContainer ? 'border-l-2 border-l-violet-200' : ''}
        ${isDragging ? 'scale-[1.02] shadow-apple border-sky-300 bg-sky-50' : ''}
        ${isSelected && !isDragging ? 'border-sky-500 bg-sky-50' : ''}
        ${!isSelected && !isDragging ? 'border-gray-200 bg-white hover:border-gray-300' : ''}
      `}
      onClick={() => onSelect(section._editorId)}
    >
      {/* Disclosure chevron (containers and block-enabled sections) */}
      {isExpandable && (
        <button
          className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-600"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpand?.(section._editorId)
          }}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault()
              e.stopPropagation()
              onToggleExpand?.(section._editorId)
            }
          }}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
          />
        </button>
      )}

      {/* Drag handle */}
      <button
        className="flex-shrink-0 cursor-grab touch-none rounded-md p-0.5 text-gray-300 hover:text-gray-500 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Index badge */}
      <div className="flex h-7 min-w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 px-1 text-xs font-medium text-gray-500">
        {indexLabel}
      </div>

      {/* Section name */}
      <span className="flex-1 truncate text-sm font-medium text-gray-700">
        {label}
      </span>

      {/* Child count badge (collapsed expandable sections) */}
      {isExpandable && !isExpanded && (childCount ?? 0) > 0 && (
        <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
          {childCount}
        </span>
      )}

      {/* Context menu (overflow "...") */}
      <div className="opacity-0 transition-opacity group-hover:opacity-100 [[data-state=open]_&]:opacity-100">
        <SectionContextMenu
          section={section}
          depth={depth}
          isContainer={isContainer}
          sections={sections}
          onMoveToContainer={onMoveToContainer}
          onMoveToRoot={onMoveToRoot}
          onAddChild={onAddChild}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onSelectFirstBlock={onSelectFirstBlock}
        />
      </div>
    </div>
  )
}
